import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Toast from "@/components/toast/Toast";
import "swiper/css";

import { GamesProvider } from "./providers/GamesProvider";
import { ConfigProvider } from "@/providers/ConfigProvider";
import { AuthProvider } from "@/providers/AuthProvider";
import { LobbyConfigProvider } from "@/providers/LobbyConfigProvider";
import ShopWidget from "./modules/shopModule/ShopWidget";
import { useAutoResizeIframe } from "./hooks/useAutoResizeIframe";
import CasinoWidgetGuard from "@/widgets/casinoWidget/CasinoWidgetGuard";
import JackpotIframeGuard from "@/widgets/casinoWidget/subWidgets/jackpotWidget/iframe/JackpotIframeGuard";

const WidgetApp = lazy(() => import("@/widgets/widgetApp/WidgetApp"));
const MissionsWidget = lazy(
  () => import("@/widgets/casinoWidget/subWidgets/missionsWidget/MissionsWidget")
);
const LobbyWidget = lazy(() => import("@/widgets/lobbyWidget/LobbyWidget"));
const CasinoHubWidget = lazy(() => import("@/widgets/casinoWidget/casinoHub/CasinoHubWidget"));
const LoyaltyWidget = lazy(() => import("@/widgets/casinoWidget/loyalty/Loyalty"));

const CompletionWidget = lazy(() => import("@/widgets/completionWidget/CompletionWidget"));

const App = () => {
  useAutoResizeIframe();
  const F2P_LOBBY_CONFIG_ROUTE = `lobby/config`;
  const CASINO_MISSIONS_LOBBY_CONFIG_ROUTE = `lobby/config/mission`;
  const CASINO_HUB_LOBBY_CONFIG_ROUTE = `lobby/config/casino`;
  const CASINO_SHOP_LOBBY_CONFIG_ROUTE = `lobby/config/shop`;
  const CASINO_RANK_MISSIONS = `lobby/config/rank`;
  const BADGES_LOBBY_CONFIG_ROUTE = `lobby/config/badge`;

  return (
    <div className="app">
      <Toast />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Soccerbet-specific surface — added as a temporary feature request. */}
            <Route
              path="/casino/completion"
              element={
                <Suspense fallback={null}>
                  <CompletionWidget />
                </Suspense>
              }
            />
            <Route path="/f2p/*">
              <Route
                path="lobby"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={F2P_LOBBY_CONFIG_ROUTE}>
                      <LobbyWidget />
                    </LobbyConfigProvider>
                  </Suspense>
                }
              />
              <Route
                path="game/*"
                element={
                  <Suspense fallback={null}>
                    <ConfigProvider>
                      <WidgetApp />
                    </ConfigProvider>
                  </Suspense>
                }
              />
            </Route>
            <Route path="/casino/*">
              <Route path="game" element={<CasinoWidgetGuard />}></Route>
              <Route path="jackpot/cards" element={<JackpotIframeGuard mode="cards" />} />
              <Route path="jackpot/fullscreen" element={<JackpotIframeGuard mode="fullscreen" />} />
              <Route
                path="missions"
                element={
                  <Suspense fallback={null}>
                    <LobbyConfigProvider dataRoute={CASINO_MISSIONS_LOBBY_CONFIG_ROUTE}>
                      <GamesProvider>
                        <MissionsWidget />
                      </GamesProvider>
                    </LobbyConfigProvider>
                  </Suspense>
                }
              ></Route>
            </Route>
            <Route
              path="/lobby/casino"
              element={
                <Suspense fallback={null}>
                  <LobbyConfigProvider dataRoute={CASINO_HUB_LOBBY_CONFIG_ROUTE}>
                    <GamesProvider>
                      <CasinoHubWidget />
                    </GamesProvider>
                  </LobbyConfigProvider>
                </Suspense>
              }
            ></Route>
            <Route
              path="/lobby/loyalty"
              element={
                <Suspense fallback={null}>
                  <LobbyConfigProvider dataRoute={CASINO_RANK_MISSIONS}>
                    <GamesProvider>
                      <LoyaltyWidget />
                    </GamesProvider>
                  </LobbyConfigProvider>
                </Suspense>
              }
            ></Route>
            <Route
              path="/casino/shop"
              element={
                <Suspense fallback={null}>
                  <LobbyConfigProvider dataRoute={CASINO_SHOP_LOBBY_CONFIG_ROUTE}>
                    <GamesProvider>
                      <ShopWidget />
                    </GamesProvider>
                  </LobbyConfigProvider>
                </Suspense>
              }
            ></Route>
            <Route
              path="/lobby/badges"
              element={
                <Suspense fallback={null}>
                  <LobbyConfigProvider dataRoute={BADGES_LOBBY_CONFIG_ROUTE}>
                    <CasinoWidgetGuard mode="badgeEndless" />
                  </LobbyConfigProvider>
                </Suspense>
              }
            ></Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
};

export default App;
